import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Bell, Trophy, ChevronRight, X, Check } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICON = {
  rank_up: '↑',
  overtaken: '↓',
  top_10_entry: '⭐',
  top_10_exit: '↘',
  new_catch_in_league: '🎣',
};

const NotificationBell = ({ token }) => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const fetchUnread = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnread(r.data.unread_count || 0);
    } catch (e) {
      // silent
    }
  }, [token]);

  const fetchList = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/notifications?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(r.data || []);
    } catch (e) {
      // silent
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [token, fetchUnread]);

  useEffect(() => {
    if (open) {
      fetchList();
    }
  }, [open, fetchList]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) {
      // ignore
    }
  };

  const markAll = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
      toast.success('All notifications marked as read');
    } catch (e) {
      toast.error('Failed');
    }
  };

  const handleClick = (n) => {
    if (!n.read) markRead(n.id);
    if (n.league_id) {
      navigate('/league');
      setOpen(false);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative p-2 text-[#8a948a] hover:text-white transition-colors"
          data-testid="notifications-bell"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span
              className="absolute top-1 right-1 w-2.5 h-2.5 bg-[#ff5e00] rounded-full ring-2 ring-[#0b0d0b]"
              data-testid="notifications-unread-dot"
            />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-80 max-h-96 overflow-y-auto bg-[#0b0d0b] border-white/10"
        data-testid="notifications-dropdown"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
          <span className="font-['Barlow_Condensed'] text-sm uppercase tracking-wider text-[#c8b273] flex items-center gap-2">
            <Trophy className="w-3.5 h-3.5" />
            Notifications
          </span>
          {items.some((n) => !n.read) && (
            <button
              onClick={markAll}
              className="text-xs text-[#8a948a] hover:text-white"
              data-testid="notifications-mark-all-read"
            >
              Mark all read
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <div className="p-6 text-center text-[#6b7d6b] text-sm">No notifications yet</div>
        ) : (
          <ul className="divide-y divide-white/5">
            {items.map((n) => (
              <li
                key={n.id}
                onClick={() => handleClick(n)}
                className={`px-3 py-3 cursor-pointer hover:bg-white/5 transition-colors ${
                  n.read ? 'opacity-60' : ''
                }`}
                data-testid={`notif-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  <span className="text-base">{TYPE_ICON[n.type] || '•'}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white">{n.message}</p>
                    <p className="text-[10px] text-[#6b7d6b] mt-0.5">
                      {new Date(n.created_at).toLocaleString()}
                    </p>
                  </div>
                  {!n.read && <span className="w-2 h-2 bg-[#ff5e00] rounded-full mt-1.5" />}
                </div>
              </li>
            ))}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
